import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Switch, Segmented, Button, Space, Spin } from 'antd';
import { PlusOutlined, MinusOutlined, ExpandOutlined } from '@ant-design/icons';
import { GoogleMap, useJsApiLoader, Marker, Polygon } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { ReplayStop } from '../graphql/types';
import { REPLAY_PRIMARY } from '../theme';
import {
    GET_ZIPCODE_MAP_BOUNDARIES_BY_AREA_ID,
    GetZipcodeMapBoundariesByAreaIdResponse,
    GetZipcodeMapBoundariesByAreaIdVariables,
} from '../../splitOrder/graphql/getZipcodeMapBoundariesByAreaId';

interface ReplayMapProps {
    stops: ReplayStop[]; // 当前展示顺序（手动模式=working order）
    startPoint?: ReplayStop | null;
    endPoint?: ReplayStop | null;
    areaId?: number; // Zipcode 围栏按区域拉取
    fitToken?: string | number | null; // 路线标识：变化时重新取景（切线路必重取景，手动重排不触发）
    // 导航模式真实轨迹：按当前顺序取轨迹点（hook 内统一走缓存 / G5 / G8）
    fetchTrajectory?: (order: ReplayStop[]) => Promise<{ lat: number; lng: number }[] | null>;
    manualMode?: boolean;
    adjustMode?: 'single' | 'range';
    anchorKey?: string | null;
    rangeKeys?: string[]; // 区间调整：当前选中段的站 key
    rangePhase?: 'pickStart' | 'pickEnd' | 'pickDest' | null;
    // 点击某个 marker：传该 marker 背后的 stop key 列表（普通点=单元素；重合簇=整簇连续 key）
    onStopClick?: (keys: string[]) => void;
}

type NavTrack = 'off' | 'line' | 'nav';

const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';
const DEFAULT_CENTER = { lat: 34.0522, lng: -118.2437 };
const DEFAULT_ZOOM = 12;
// 缩放层级 ≥ 此值时自动显示所有 stop 序号（放大到足够近，序号不再互相遮挡）
const STOP_LABEL_ZOOM = 12;

const SIMPLE_MAP_STYLES: google.maps.MapTypeStyle[] = [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'landscape', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const isValid = (p?: { latitude: number; longitude: number } | null) =>
    !!p && p.latitude != null && p.longitude != null && !(p.latitude === 0 && p.longitude === 0);

const toLatLng = (p: { latitude: number; longitude: number }) => ({ lat: p.latitude, lng: p.longitude });

// Icon 按内容缓存：同一图标始终返回同一对象引用，避免每次渲染 new 出新对象
// 导致 <Marker> 全量 setIcon 重绘（hover 时所有点一起抖）
const iconCache = new Map<string, google.maps.Icon>();
const cachedIcon = (key: string, build: () => google.maps.Icon): google.maps.Icon => {
    let icon = iconCache.get(key);
    if (!icon) {
        icon = build();
        iconCache.set(key, icon);
    }
    return icon;
};

// 序号字号按长度自适应：单点 1-2 位正常，重合簇如 73-74（5 位）缩小塞进同尺寸圆里
const dotFontSize = (label: string): number => {
    const n = label.length;
    if (n <= 2) return 9;
    if (n === 3) return 7.5;
    if (n === 4) return 6.3;
    return 5.4;
};
const anchorFontSize = (label: string): number => {
    const n = label.length;
    if (n <= 2) return 11;
    if (n === 3) return 9;
    if (n === 4) return 8;
    return 7;
};

// 停靠点：蓝白黑同心圆；hover / 需要时中间显示白色序号。size 随缩放变化（进缓存 key）
// 重合簇复用同款圆，label 为区间号（如 73-74），字号自适应，不做 hover 放大
const stopDotIcon = (label: string | undefined, size: number): google.maps.Icon =>
    cachedIcon(`dot:${label ?? ''}:${size}`, () => ({
        url:
            'data:image/svg+xml;charset=UTF-8,' +
            encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 26 26"><circle cx="13" cy="13" r="12" fill="#111827"/><circle cx="13" cy="13" r="9" fill="#ffffff"/><circle cx="13" cy="13" r="8" fill="${REPLAY_PRIMARY}"/>${
                    label ? `<text x="13" y="16" font-size="${dotFontSize(label)}" fill="#ffffff" text-anchor="middle" font-family="Arial" font-weight="bold">${label}</text>` : ''
                }</svg>`
            ),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
    }));

// 锚点：橙色高亮 + 白色序号（区间号同样字号自适应）。比停靠点略大
const anchorIcon = (label: string, size: number): google.maps.Icon =>
    cachedIcon(`anchor:${label}:${size}`, () => ({
        url:
            'data:image/svg+xml;charset=UTF-8,' +
            encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 30 30"><circle cx="15" cy="15" r="14" fill="#ffffff"/><circle cx="15" cy="15" r="12" fill="#f59e0b"/><text x="15" y="19" font-size="${anchorFontSize(label)}" fill="#ffffff" text-anchor="middle" font-family="Arial" font-weight="bold">${label}</text></svg>`
            ),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
    }));

// 起点/终点
const labelIcon = (fill: string, text: string, size = 30): google.maps.Icon =>
    cachedIcon(`label:${fill}:${text}:${size}`, () => ({
        url:
            'data:image/svg+xml;charset=UTF-8,' +
            encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 26 26"><circle cx="13" cy="13" r="12" fill="${fill}" stroke="#fff" stroke-width="2"/><text x="13" y="17" font-size="11" fill="#fff" text-anchor="middle" font-family="Arial" font-weight="bold">${text}</text></svg>`
            ),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
    }));

/** 右侧地图区（Google Maps）+ 图层面板 + 控件 */
const ReplayMap: React.FC<ReplayMapProps> = ({
    stops,
    startPoint,
    endPoint,
    areaId,
    fitToken,
    fetchTrajectory,
    manualMode = false,
    adjustMode = 'single',
    anchorKey,
    rangeKeys = [],
    rangePhase,
    onStopClick,
}) => {
    const { t } = useTranslation();
    const [showParcel, setShowParcel] = useState(true);
    const [navTrack, setNavTrack] = useState<NavTrack>('line');
    const [showZipcode, setShowZipcode] = useState(true);
    const [simpleMap, setSimpleMap] = useState(true);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
    const [hovered, setHovered] = useState<string | null>(null);
    // hover 到某个 zipcode 围栏时的信息 + 屏幕坐标（画 tooltip）
    const [hoveredZipcode, setHoveredZipcode] = useState<{ zipcode: string; x: number; y: number } | null>(null);
    const polylinesRef = useRef<google.maps.Polyline[]>([]);
    const previewRef = useRef<google.maps.Polyline | null>(null);
    const navPolyRef = useRef<google.maps.Polyline | null>(null);
    const [navPath, setNavPath] = useState<google.maps.LatLngLiteral[] | null>(null);
    const [navLoading, setNavLoading] = useState(false);

    // Zipcode 围栏（按区域）：始终拉取——既用于图层绘制，也用于计算初始视野（围栏 + 仓库）
    const { data: zipcodeData } = useQuery<GetZipcodeMapBoundariesByAreaIdResponse, GetZipcodeMapBoundariesByAreaIdVariables>(
        GET_ZIPCODE_MAP_BOUNDARIES_BY_AREA_ID,
        { variables: { areaId: areaId as number }, skip: areaId == null }
    );

    // 每个围栏保留 zipcode + 边界点（hover tooltip 要用 zipcode）
    const zipcodeBoundaries = useMemo(() => {
        const items = zipcodeData?.getZipcodeMapBoundariesByAreaId;
        const out: { zipcode: string; paths: google.maps.LatLngLiteral[] }[] = [];
        if (!items?.length) return out;
        for (const item of items) {
            try {
                const coords = JSON.parse(item.boundary);
                const rawPath = Array.isArray(coords?.[0]) ? coords[0] : coords;
                const path = (rawPath as any[])
                    .filter((p: any) => typeof p.lat === 'number' && typeof p.lng === 'number')
                    .map((p: any) => ({ lat: p.lat, lng: p.lng }));
                if (path.length) out.push({ zipcode: item.zipcode, paths: path });
            } catch (_e) {
                // 解析失败静默跳过
            }
        }
        return out;
    }, [zipcodeData]);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        preventGoogleFontsLoading: true,
    });

    const onLoad = useCallback((m: google.maps.Map) => {
        setMap(m);
        mapRef.current = m;
        setZoomLevel(m.getZoom() ?? DEFAULT_ZOOM);
    }, []);
    // 缩放变化用声明式回调（比 onLoad 里手动 addListener 稳，闭包不会失效）：
    // 序号是否显示依赖 zoomLevel，这里必须实时同步真实缩放层级
    const onZoomChanged = useCallback(() => {
        setZoomLevel(mapRef.current?.getZoom() ?? DEFAULT_ZOOM);
    }, []);
    const onUnmount = useCallback(() => {
        mapRef.current = null;
        setMap(null);
    }, []);

    // geometry 库：containsLocation 命中判断需要
    useEffect(() => {
        if (isLoaded) (google.maps as unknown as { importLibrary?: (n: string) => Promise<unknown> }).importLibrary?.('geometry').catch(() => {});
    }, [isLoaded]);

    // 鼠标在地图上移动：命中哪个 zipcode 围栏就高亮 + 记录屏幕坐标画 tooltip
    // 手动调整模式下不做围栏高亮（避免和点站交互抢注意力）
    const handleMouseMove = useCallback((e: google.maps.MapMouseEvent) => {
        if (manualMode || !showZipcode || !e.latLng || !zipcodeBoundaries.length) {
            setHoveredZipcode((prev) => (prev ? null : prev));
            return;
        }
        const latLng = e.latLng;
        const domEvent = e.domEvent as MouseEvent;
        for (const b of zipcodeBoundaries) {
            if (b.paths.length >= 3 && google.maps.geometry?.poly?.containsLocation?.(latLng, new google.maps.Polygon({ paths: b.paths }))) {
                setHoveredZipcode({ zipcode: b.zipcode, x: domEvent.clientX, y: domEvent.clientY });
                return;
            }
        }
        setHoveredZipcode((prev) => (prev ? null : prev));
    }, [manualMode, showZipcode, zipcodeBoundaries]);

    // 进手动模式立即清掉可能残留的围栏高亮（不等下一次鼠标移动）
    useEffect(() => {
        if (manualMode) setHoveredZipcode(null);
    }, [manualMode]);

    const zoom = (delta: number) => map?.setZoom((map.getZoom() ?? DEFAULT_ZOOM) + delta);

    const validStops = useMemo(() => stops.filter((s) => isValid(s)), [stops]);
    // 稳定的 position 引用：stops 不变就复用同一对象，避免每次渲染 new 出新对象
    // 触发 <Marker> 全量 setPosition 重定位（hover 时所有点一起抖）
    const stopPositions = useMemo(
        () => new Map(validStops.map((s) => [s.stop, toLatLng(s)])),
        [validStops]
    );
    // 重合簇分组：把「经纬度相同且序号连续」的停靠点合成一组，一组画一个 marker（区间序号 lo-hi）
    // 连续同坐标在当前顺序里天然是一段连续区间，避免重叠 marker 点不中
    const stopGroups = useMemo(() => {
        const groups: { lat: number; lng: number; startIdx: number; endIdx: number; memberKeys: string[] }[] = [];
        validStops.forEach((s, i) => {
            const prev = groups[groups.length - 1];
            if (prev && prev.lat === s.latitude && prev.lng === s.longitude) {
                prev.endIdx = i;
                prev.memberKeys.push(s.stop);
            } else {
                groups.push({ lat: s.latitude, lng: s.longitude, startIdx: i, endIdx: i, memberKeys: [s.stop] });
            }
        });
        return groups;
    }, [validStops]);

    const startPos = useMemo(() => (isValid(startPoint) ? toLatLng(startPoint!) : null), [startPoint]);
    const endPos = useMemo(() => (isValid(endPoint) ? toLatLng(endPoint!) : null), [endPoint]);

    // 直线轨迹路径：起点 → 各停靠点 → 终点
    const linePath = useMemo(() => {
        const pts: google.maps.LatLngLiteral[] = [];
        if (isValid(startPoint)) pts.push(toLatLng(startPoint!));
        validStops.forEach((s) => pts.push(toLatLng(s)));
        if (isValid(endPoint)) pts.push(toLatLng(endPoint!));
        return pts;
    }, [startPoint, endPoint, validStops]);

    const segments = useMemo(() => {
        const segs: google.maps.LatLngLiteral[][] = [];
        for (let i = 0; i < linePath.length - 1; i++) segs.push([linePath[i], linePath[i + 1]]);
        return segs;
    }, [linePath]);

    // 初始视野锚点（优先级）：停靠点（起终点+各站）> zipcode 围栏兜底
    // 对齐 routes-history：按实际投递点取景，围栏范围远大于站点，用围栏会让 marker 偏小
    const framePts = useMemo(() => {
        if (linePath.length) return linePath;
        const pts: google.maps.LatLngLiteral[] = [];
        zipcodeBoundaries.forEach((b) => b.paths.forEach((p) => pts.push(p)));
        return pts;
    }, [linePath, zipcodeBoundaries]);

    // fit：对齐 routes-history 的 fitBounds(bounds, 60)——四边各留 60px 内边距，取景更紧、marker 更大
    const fitToFrame = useCallback(() => {
        if (!map || framePts.length === 0) return;
        const bounds = new google.maps.LatLngBounds();
        framePts.forEach((p) => bounds.extend(p));
        map.fitBounds(bounds, 60);
    }, [map, framePts]);

    const resetView = () => fitToFrame();

    // 仅当所选视野锚点源变化时重新 fit（手动重排不 fit，避免视野乱跳）
    const frameKey = `${fitToken ?? ''}:${linePath.length ? `stops:${linePath.length}` : `fence:${areaId ?? ''}:${zipcodeBoundaries.length}`}`;
    useEffect(() => {
        if (!map || !isLoaded) return;
        fitToFrame();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, isLoaded, frameKey]);

    // 直线连线（命令式，避免 <Polyline> 卸载残留）——仅「直线」档
    useEffect(() => {
        polylinesRef.current.forEach((p) => p.setMap(null));
        polylinesRef.current = [];
        if (!map || navTrack !== 'line') return;
        polylinesRef.current = segments.map(
            (seg) =>
                new google.maps.Polyline({
                    path: seg,
                    strokeColor: REPLAY_PRIMARY,
                    strokeWeight: 3,
                    strokeOpacity: 0.9,
                    clickable: false,
                    icons: [
                        {
                            icon: {
                                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                scale: 3,
                                strokeColor: REPLAY_PRIMARY,
                                strokeWeight: 1,
                                fillColor: REPLAY_PRIMARY,
                                fillOpacity: 1,
                            },
                            offset: '50%',
                        },
                    ],
                    map,
                })
        );
        return () => {
            polylinesRef.current.forEach((p) => p.setMap(null));
            polylinesRef.current = [];
        };
    }, [map, segments, navTrack]);

    // 导航档：按当前顺序取真实轨迹（hook 内走 orderKey 缓存 / G5 / G8），顺序变化重新取
    const orderedKey = validStops.map((s) => s.stop).join(',');
    useEffect(() => {
        if (navTrack !== 'nav' || !fetchTrajectory || validStops.length === 0) {
            setNavPath(null);
            setNavLoading(false);
            return;
        }
        let cancelled = false;
        setNavLoading(true);
        fetchTrajectory(validStops)
            .then((pts) => {
                if (cancelled) return;
                const path = pts && pts.length >= 2 ? pts.map((p) => ({ lat: p.lat, lng: p.lng })) : null;
                setNavPath(path);
            })
            .catch(() => {
                if (!cancelled) {
                    setNavPath(null);
                }
            })
            .finally(() => {
                if (!cancelled) setNavLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navTrack, orderedKey]);

    // 导航轨迹 polyline（命令式）
    useEffect(() => {
        navPolyRef.current?.setMap(null);
        navPolyRef.current = null;
        if (!map || navTrack !== 'nav' || !navPath || navPath.length < 2) return;
        navPolyRef.current = new google.maps.Polyline({
            path: navPath,
            strokeColor: REPLAY_PRIMARY,
            strokeWeight: 4,
            strokeOpacity: 0.9,
            clickable: false,
            icons: [
                {
                    icon: {
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 2.5,
                        strokeColor: REPLAY_PRIMARY,
                        fillColor: REPLAY_PRIMARY,
                        fillOpacity: 1,
                    },
                    offset: '0',
                    repeat: '120px',
                },
            ],
            map,
        });
        return () => {
            navPolyRef.current?.setMap(null);
            navPolyRef.current = null;
        };
    }, [map, navTrack, navPath]);

    // 预览起点：单点=锚点；区间(点目的地阶段)=区间最后一站
    const previewFromKey = useMemo(() => {
        if (!manualMode || !hovered) return null;
        if (adjustMode === 'single') {
            return anchorKey && hovered !== anchorKey ? anchorKey : null;
        }
        // range：仅「点目的地」阶段、且 hover 站不在区间内
        if (rangePhase === 'pickDest' && !rangeKeys.includes(hovered)) {
            return rangeKeys[rangeKeys.length - 1] ?? null;
        }
        return null;
    }, [manualMode, adjustMode, anchorKey, rangePhase, rangeKeys, hovered]);

    // 起点 → hover 站 的橙色虚线预览
    useEffect(() => {
        previewRef.current?.setMap(null);
        previewRef.current = null;
        if (!map || !previewFromKey || !hovered) return;
        const a = validStops.find((s) => s.stop === previewFromKey);
        const h = validStops.find((s) => s.stop === hovered);
        if (!a || !h) return;
        previewRef.current = new google.maps.Polyline({
            path: [toLatLng(a), toLatLng(h)],
            strokeOpacity: 0,
            clickable: false,
            zIndex: 5,
            icons: [
                {
                    icon: { path: 'M 0,-1 0,1', strokeColor: '#f59e0b', strokeOpacity: 1, strokeWeight: 3, scale: 2 },
                    offset: '0',
                    repeat: '9px',
                },
                {
                    icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3, strokeColor: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 1 },
                    offset: '100%',
                },
            ],
            map,
        });
        return () => {
            previewRef.current?.setMap(null);
            previewRef.current = null;
        };
    }, [map, previewFromKey, hovered, validStops]);

    const parcelLocked = manualMode;
    const parcelVisible = parcelLocked ? true : showParcel;

    // marker 尺寸随缩放级别平滑变化：整体视野不小于原尺寸(32)，放大越近越大，封顶 56 避免过大
    const dotSize = Math.max(32, Math.min(56, Math.round(32 + (zoomLevel - 11) * 6)));
    const anchorSize = dotSize + 8;

    return (
        <div className="relative w-full h-full bg-[#e6eaf2] overflow-hidden">
            {loadError ? (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">{t('delivery.routeReplay.map.loadFailed')}</div>
            ) : !isLoaded ? (
                <div className="absolute inset-0 flex items-center justify-center">
                    <Spin tip={t('delivery.routeReplay.map.loading')} />
                </div>
            ) : (
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={DEFAULT_CENTER}
                    zoom={DEFAULT_ZOOM}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    onZoomChanged={onZoomChanged}
                    clickableIcons={false}
                    onMouseMove={handleMouseMove}
                    options={{ disableDefaultUI: true, styles: simpleMap ? SIMPLE_MAP_STYLES : [] }}
                >
                    {/* Zipcode 围栏（hover 高亮，见 handleMouseMove + tooltip） */}
                    {showZipcode &&
                        zipcodeBoundaries.map((b) => {
                            const isHovered = hoveredZipcode?.zipcode === b.zipcode;
                            return (
                                <Polygon
                                    key={`zb-${b.zipcode}`}
                                    paths={b.paths}
                                    options={{
                                        fillColor: REPLAY_PRIMARY,
                                        fillOpacity: isHovered ? 0.28 : 0.06,
                                        strokeColor: REPLAY_PRIMARY,
                                        strokeOpacity: isHovered ? 1 : 0.7,
                                        strokeWeight: isHovered ? 2.5 : 1.5,
                                        clickable: false,
                                        zIndex: 1,
                                    }}
                                />
                            );
                        })}

                    {/* 停靠点（重合簇合成一个 marker） */}
                    {parcelVisible &&
                        stopGroups.map((g) => {
                            const startNo = g.startIdx + 1;
                            const endNo = g.endIdx + 1;
                            const isRange = g.endIdx > g.startIdx;
                            const label = isRange ? `${startNo}-${endNo}` : `${startNo}`;
                            // 单点=锚点在簇内即高亮；区间=选中段与簇有交集即高亮
                            const isHi =
                                manualMode &&
                                (adjustMode === 'single'
                                    ? anchorKey != null && g.memberKeys.includes(anchorKey)
                                    : g.memberKeys.some((k) => rangeKeys.includes(k)));
                            const isHovered = hovered != null && g.memberKeys.includes(hovered);
                            const showNum = isHi || isHovered || zoomLevel >= STOP_LABEL_ZOOM;
                            // 重合簇与单点统一用圆形，label 为区间号（字号自适应）；不 hover 放大
                            const icon = isHi
                                ? anchorIcon(label, anchorSize)
                                : stopDotIcon(showNum ? label : undefined, dotSize);
                            return (
                                <Marker
                                    key={g.memberKeys[0]}
                                    position={stopPositions.get(g.memberKeys[0])!}
                                    icon={icon}
                                    onMouseOver={() => setHovered(g.memberKeys[0])}
                                    onMouseOut={() => setHovered(null)}
                                    onClick={manualMode ? () => onStopClick?.(g.memberKeys) : undefined}
                                    zIndex={isHi ? 300 : isHovered ? 200 : 100}
                                />
                            );
                        })}


                    {/* 起点 / 终点（来自 G4 JSON；缺失则不渲染） */}
                    {startPos && (
                        <Marker position={startPos} icon={labelIcon('#16a34a', t('delivery.routeReplay.map.start'), dotSize)} zIndex={999} />
                    )}
                    {endPos && (
                        <Marker position={endPos} icon={labelIcon('#111827', t('delivery.routeReplay.map.end'), dotSize)} zIndex={999} />
                    )}
                </GoogleMap>
            )}

            {/* Zipcode 围栏 hover tooltip（跟随鼠标） */}
            {hoveredZipcode && (
                <div
                    style={{
                        position: 'fixed',
                        left: hoveredZipcode.x + 12,
                        top: hoveredZipcode.y - 40,
                        pointerEvents: 'none',
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            background: 'rgba(0,0,0,0.75)',
                            color: '#fff',
                            padding: '6px 10px',
                            borderRadius: 6,
                            fontSize: 13,
                            lineHeight: '1.5',
                            whiteSpace: 'nowrap',
                            fontWeight: 600,
                        }}
                    >
                        {hoveredZipcode.zipcode}
                    </div>
                </div>
            )}

            {/* 图层面板 —— 左下 */}
            <Card
                size="small"
                title={<span className="text-xs text-gray-500">{t('delivery.routeReplay.map.layer')}</span>}
                className="absolute left-3 bottom-3 w-52 shadow-lg"
                styles={{ header: { minHeight: 32, padding: '0 12px' }, body: { padding: '8px 12px' } }}
            >
                <div className="flex items-center justify-between py-1">
                    <span className="text-[13px] text-gray-700">
                        {t('delivery.routeReplay.map.parcelPosition')}
                        {parcelLocked && <span className="ml-1 text-[11px] text-orange-500">{t('delivery.routeReplay.map.lockedInAdjust')}</span>}
                    </span>
                    <Switch size="small" checked={parcelVisible} disabled={parcelLocked} onChange={setShowParcel} />
                </div>

                <div className="flex items-center justify-between py-1">
                    <span className="text-[13px] text-gray-700">
                        {t('delivery.routeReplay.map.navTrack')}
                        {navLoading && <Spin size="small" className="ml-1" />}
                    </span>
                    <Segmented
                        size="small"
                        className="[&_.ant-segmented-item-label]:font-semibold [&_.ant-segmented-item-label]:text-xs"
                        value={navTrack}
                        onChange={(v) => setNavTrack(v as NavTrack)}
                        options={[
                            { label: t('delivery.routeReplay.map.navOff'), value: 'off' },
                            { label: t('delivery.routeReplay.map.navLine'), value: 'line' },
                            { label: t('delivery.routeReplay.map.navRoute'), value: 'nav' },
                        ]}
                    />
                </div>

                <div className="flex items-center justify-between py-1">
                    <span className="text-[13px] text-gray-700">{t('delivery.routeReplay.map.zipcodeFence')}</span>
                    <Switch size="small" checked={showZipcode} onChange={setShowZipcode} />
                </div>

                <div className="flex items-center justify-between py-1">
                    <span className="text-[13px] text-gray-700">{t('delivery.routeReplay.map.simpleMap')}</span>
                    <Switch size="small" checked={simpleMap} onChange={setSimpleMap} />
                </div>
            </Card>

            {/* 控件 —— 右下 */}
            <div className="absolute right-3 bottom-3">
                <Space.Compact direction="vertical">
                    <Button icon={<PlusOutlined />} onClick={() => zoom(1)} />
                    <Button icon={<MinusOutlined />} onClick={() => zoom(-1)} />
                    <Button icon={<ExpandOutlined />} onClick={resetView} />
                </Space.Compact>
            </div>
        </div>
    );
};

export default ReplayMap;
